import {
  type CSSProperties,
  type ComponentPropsWithoutRef,
  type ReactElement,
  useId,
} from 'react';

import classNames from 'classnames';

import { ChartPatterns } from './ChartPatterns';
import {
  AMBIENT_DURATION_MILLISECONDS,
  CHART_SHAPES,
  type ChartShape,
  ChartShapeGeometry,
} from './ChartShapeGeometry';

import styles from './ChartGraphic.module.css';

export type ChartGraphicProps = ComponentPropsWithoutRef<'svg'>;

const getShapeDriftStyle = (shape: ChartShape): CSSProperties => ({
  animationDuration: `${
    AMBIENT_DURATION_MILLISECONDS * shape.driftDurationMultiplier
  }ms`,
});

export const ChartGraphic = ({
  className,
  ...props
}: ChartGraphicProps): ReactElement => {
  const graphicId = useId();
  const id = (name: string): string => `${graphicId}-${name}`;

  return (
    <svg
      viewBox="0 0 1440 900"
      width="100%"
      height="100%"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid slice"
      {...props}
      aria-hidden="true"
      focusable="false"
      className={classNames(styles.canvas, className)}
    >
      <defs>
        <ChartPatterns getId={id} />

        {CHART_SHAPES.map(shape => (
          <symbol key={shape.id} id={id(shape.id)} viewBox={shape.viewBox}>
            <ChartShapeGeometry shape={shape} />
          </symbol>
        ))}

        {CHART_SHAPES.map(shape => (
          <g key={shape.id} id={id(`${shape.id}-asset`)}>
            <use href={`#${id(shape.id)}`} {...shape.assetPlacement} />
          </g>
        ))}

        {CHART_SHAPES.map(shape => (
          <g key={shape.id} id={id(`${shape.id}-window`)}>
            <rect {...shape.textureWindow} />
          </g>
        ))}

        <mask
          id={id('texture-mask')}
          maskUnits="userSpaceOnUse"
          x="0"
          y="0"
          width="1440"
          height="900"
          className={styles.textureMask}
        >
          <rect width="1440" height="900" fill="white" />
          {CHART_SHAPES.map(shape => (
            <use
              key={shape.id}
              href={`#${id(`${shape.id}-window`)}`}
              fill="black"
            />
          ))}
        </mask>
      </defs>

      {CHART_SHAPES.map(shape => (
        <use
          key={shape.id}
          href={`#${id(`${shape.id}-asset`)}`}
          fill={`url(#${id(shape.patternId)})`}
          className={styles[shape.driftClassName]}
          style={getShapeDriftStyle(shape)}
        />
      ))}

      <g mask={`url(#${id('texture-mask')})`}>
        {CHART_SHAPES.map(shape => (
          <use
            key={shape.id}
            href={`#${id(`${shape.id}-asset`)}`}
            className={classNames(
              styles.blueFill,
              styles[shape.driftClassName],
            )}
            style={getShapeDriftStyle(shape)}
          />
        ))}
      </g>

      {CHART_SHAPES.map(shape => (
        <use
          key={shape.id}
          href={`#${id(`${shape.id}-window`)}`}
          className={styles.frame}
        />
      ))}
    </svg>
  );
};
